/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { ChevronLeft, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useEffect, useState } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import ReactPlayer from 'react-player';
import { useNavigate, useParams } from 'react-router-dom';
import { actions } from '../../actions';
import dataProvider from '../../authentication/dataProvider';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { useToast } from '../../components/ui/use-toast';
import { createAdminNotification } from '../../services/notificationService';
import { getFileType } from '../../helper/function';
import { services } from '../../services';
import VideoCover from '../../video-cover.jpg';
import { web3 } from '../../web3';

interface RenderNFTProps {
  image?: {
    format?: string;
    compressed?: string;
    original?: string;
  };
}

const RenderNFT: React.FC<RenderNFTProps> = (record) => {
  const [ext, setExt] = useState<string | false>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let isMounted = true;
    setIsLoading(true);

    async function getExtenstion(): Promise<void> {
      try {
        if (!record?.image?.format) {
          const extenstion = await getFileType(record?.image?.compressed);
          if (isMounted) {
            setExt(extenstion);
            setIsLoading(false);
          }
        } else {
          if (isMounted) {
            setExt(record.image.format);
            setIsLoading(false);
          }
        }
      } catch (error) {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    }

    getExtenstion();

    return () => {
      isMounted = false;
    };
  }, [record, record?.image?.compressed, record?.image?.format]);

  if (isLoading) {
    return (
      <div className='flex items-center justify-center h-[250px] w-[250px]'>
        <Loader2 className='h-8 w-8 animate-spin text-primary' />
      </div>
    );
  }

  if (ext && ext === 'image')
    return (
      <img
        src={record.image?.original}
        alt='Asset image'
        className='h-[250px] w-[250px] object-contain rounded-lg'
      />
    );
  if (ext && ext === 'audio') return <ReactAudioPlayer src={record.image?.compressed} controls />;
  if (ext && ext === 'video')
    return (
      <ReactPlayer
        controls={true}
        url={record.image?.original}
        playIcon={<></>}
        width='450px'
        height='250px'
        light={VideoCover}
      />
    );
  return (
    <div className='flex items-center justify-center h-[250px] w-[250px] bg-gray-100 dark:bg-gray-800 rounded-lg'>
      <p className='text-sm text-gray-500'>No media available</p>
    </div>
  );
};

const AssetShow = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const [loading, setLoading] = useState<boolean>(true);
  const [record, setRecord] = useState<any>(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [yieldValue, setYieldValue] = useState<string>('');
  const [rewardValue, setRewardValue] = useState<string>('');
  const [web3Data, setWeb3Data] = useState(null);
  const [assetImplimentationContract, setAssetImplimentationContract] = useState(null);
  const [tokenContract, setTokenContract] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Replace with your actual API call
        const response = await dataProvider.getOne('assets', { id });
        if (Object.keys(response.data).length > 0) {
          setLoading(false);
        }
        setRecord(response.data);
      } catch (error) {
        setLoading(false);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id]);

  useEffect(() => {
    const slider = document.querySelector('.snap-x');
    if (slider) {
      const handleScroll = () => {
        const slideWidth = slider.clientWidth;
        const newSlide = Math.round(slider.scrollLeft / slideWidth);
        setCurrentSlide(newSlide);
      };

      slider.addEventListener('scroll', handleScroll);
      return () => slider.removeEventListener('scroll', handleScroll);
    }
  }, []);

  useEffect(() => {
    async function initializeData() {
      try {
        setLoading(true);

        // First initialize web3
        const web3Data = await services.getWeb3();
        if (!web3Data || !web3Data.accounts || !web3Data.accounts[0]) {
          // Wallet not connected - this is normal, don't throw error
          setLoading(false);
          return;
        }
        setWeb3Data(web3Data);

        const tokenInstance = await actions.getTokenContract();
        if (!tokenInstance) {
          throw new Error('Failed to initialize token contract');
        }

        setTokenContract(tokenInstance);

        // Then get contract instance
        const contractInstance = await actions.getPropertyAssetContract(record?.assetAddress);
        if (!contractInstance) {
          throw new Error('Failed to initialize contract');
        }
        setAssetImplimentationContract(contractInstance);
        setLoading(false);
      } catch (error) {
        // Only show toast if it's not a wallet connection issue
        if (!error.message?.includes('Please connect your wallet first')) {
          toast({
            description: 'Failed to initialize: ' + error.message,
            variant: 'destructive',
          });
        }
        setLoading(false);
      }
    }

    initializeData();
  }, [record, toast]);

  if (loading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <Loader2 className='h-8 w-8 animate-spin' />
      </div>
    );
  }

  const handleYieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setYieldValue(value);
  };

  const handleRewardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setRewardValue(value);
  };

  const updateYield = async () => {
    const numericValue = parseFloat(yieldValue);

    if (isNaN(numericValue)) {
      toast({
        variant: 'destructive',
        description: 'Please enter a valid number',
      });
      return;
    }

    if (numericValue < 0 || numericValue > 100) {
      toast({
        variant: 'destructive',
        description: 'Yield must be between 0 and 100',
      });
      return;
    }

    try {
      if (!web3Data || !web3Data.accounts[0]) {
        toast({
          variant: 'destructive',
          description: 'Please connect your wallet first.',
        });
        return;
      }

      if (!assetImplimentationContract) {
        toast({
          variant: 'destructive',
          description: 'Contract not initialized. Please check your wallet connection.',
        });
        return;
      }

      await assetImplimentationContract.methods
        .updateAssetYield(yieldValue)
        .send({ from: web3Data.accounts[0] })
        .on('transactionHash', () => {
          toast({
            description: 'Please wait for confirmation...',
            variant: 'destructive',
          });
        })
        .on('receipt', () => {
          setYieldValue('');
          toast({
            description: 'Yield updated successfully',
            variant: 'default',
          });
          createAdminNotification({
            type: 'yield',
            title: 'Yield updated',
            message: `${record?.name || record?.assetAddress || 'Asset'} yield was updated.`,
            priority: 'medium',
            assetId: record?._id,
          });
        })
        .on('error', (error) => {
          toast({
            variant: 'destructive',
            description: error.message || 'Failed to update yield',
          });
        });
      // eslint-disable-next-line no-empty
    } catch (error) {
      console.error('Error updating yield:', error);
      // Error handled above with toast notification
      return; // Early return on error
    }
  };

  const addReward = async () => {
    const numericValue = parseFloat(rewardValue);
    if (isNaN(numericValue)) {
      toast({
        variant: 'destructive',
        description: 'Please enter a valid number',
      });
      return;
    }

    const amountInWei = web3?.utils.toWei(
      record?.AssetDetials?.financial?.totalTokenValue.toString(),
      'ether'
    );

    const getAllowance = await tokenContract?.methods
      .allowance(web3Data.accounts[0], record?.assetAddress)
      .call();
    if (Number(getAllowance) === 0 || Number(getAllowance) < Number(amountInWei)) {
      const maxUint256 =
        '115792089237316195423570985008687907853269984665640564039457584007913129639935';
      await tokenContract.methods
        .approve(record?.assetAddress, maxUint256)
        .send({ from: web3Data.accounts[0] })
        .on('transactionHash', (hash) => {
          toast({
            description: 'Please wait for approval...',
          });
        })
        .on('receipt', () => {
          toast({
            variant: 'default',
            description: 'approved successfully',
          });
          setRewardValue('');
        })
        .on('error', (error) => {
          toast({
            variant: 'destructive',
            description: error.message || 'Failed to approval reward.',
          });
        });
    }

    await assetImplimentationContract.methods
      .addReward(web3.utils.toWei(rewardValue.toString(), 'ether'))
      .send({ from: web3Data.accounts[0] })
      .on('transactionHash', (hash) => {
        toast({
          description: 'Please wait for confirmation...',
        });
      })
      .on('receipt', () => {
        toast({
          variant: 'default',
          description: 'Reward added successfully',
        });
        createAdminNotification({
          type: 'yield',
          title: 'Reward added',
          message: `Reward was added to ${record?.name || record?.assetAddress || 'asset'}.`,
          priority: 'medium',
          assetId: record?._id,
        });
        setRewardValue('');
      })
      .on('error', (error) => {
        toast({
          variant: 'destructive',
          description: error.message || 'Failed to add reward',
        });
      });
  };

  return (
    <div className='space-y-6 p-6'>
      <div className='flex items-center gap-4'>
        <Button variant='outline' size='icon' onClick={() => navigate(-1)}>
          <ChevronLeft className='h-4 w-4' />
        </Button>
        <h2 className='text-2xl font-bold'>Asset Details</h2>
      </div>

      <Card className='shadow-lg'>
        <CardContent className='p-6'>
          <Tabs defaultValue='asset-info'>
            <TabsList className='flex justify-start border-b mb-6'>
              <TabsTrigger value='asset-info'>Asset Info</TabsTrigger>
              <TabsTrigger value='transaction'>Transaction Details</TabsTrigger>
              {/* <TabsTrigger value='yield'>Yield Details</TabsTrigger> */}
            </TabsList>

            <TabsContent value='asset-info' className='space-y-4'>
              <div className='grid gap-4'>
                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Asset Name
                  </h4>
                  <p className='text-lg font-medium'>{record?.assetName}</p>
                </div>

                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Asset Image
                  </h4>
                  <div className='flex flex-col gap-4'>
                    <div className='relative max-w-[600px] mx-0'>
                      <div className='overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm'>
                        <div className='flex snap-x snap-mandatory overflow-x-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none] relative'>
                          {/* First show the main image */}
                          <div className='snap-center shrink-0 w-full flex flex-col'>
                            <div className='relative h-[250px] w-full flex justify-center items-center'>
                              <img
                                src={record?.image?.original}
                                alt='Main Asset Image'
                                className='h-full max-w-full object-contain rounded-lg'
                              />
                            </div>
                            <div className='bg-gray-50/50 dark:bg-gray-800/50 p-2 text-center'>
                              <p className='text-sm text-gray-600 dark:text-gray-400'>Main Image</p>
                            </div>
                          </div>

                          {/* Then show all side images */}
                          {record?.image?.sideImages &&
                            record.image.sideImages.map((sideImage, index) => (
                              <div
                                key={index}
                                className='snap-center shrink-0 w-full flex flex-col'
                              >
                                <div className='relative h-[250px] w-full flex justify-center items-center'>
                                  <img
                                    src={sideImage.url}
                                    alt={sideImage.description || `Side image ${index + 1}`}
                                    className='h-full max-w-full object-contain rounded-lg'
                                  />
                                </div>
                                {sideImage.description && (
                                  <div className='bg-gray-50/50 dark:bg-gray-800/50 p-2 text-center'>
                                    <p className='bg-gray-50/50 dark:bg-gray-800/50 text-sm'>
                                      {sideImage.description}
                                    </p>
                                  </div>
                                )}
                              </div>
                            ))}
                        </div>

                        {/* Left arrow navigation */}
                        <button
                          className='absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 rounded-full p-2 shadow-md hover:bg-white dark:hover:bg-gray-800 focus:outline-none z-10'
                          onClick={() => {
                            const slider = document.querySelector('.snap-x');
                            if (slider) {
                              const slideWidth = slider.clientWidth;
                              const prevSlide = currentSlide - 1;
                              const targetSlide = prevSlide < 0 ? 0 : prevSlide;
                              slider.scrollTo({
                                left: slideWidth * targetSlide,
                                behavior: 'smooth',
                              });
                              setCurrentSlide(targetSlide);
                            }
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-5 w-5'
                            viewBox='0 0 20 20'
                            fill='currentColor'
                          >
                            <path
                              fillRule='evenodd'
                              d='M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </button>

                        {/* Right arrow navigation */}
                        <button
                          className='absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 dark:bg-gray-800/80 rounded-full p-2 shadow-md hover:bg-white dark:hover:bg-gray-800 focus:outline-none z-10'
                          onClick={() => {
                            const slider = document.querySelector('.snap-x');
                            if (slider) {
                              const slideWidth = slider.clientWidth;
                              const totalSlides = 1 + (record?.image?.sideImages?.length || 0);
                              const nextSlide = currentSlide + 1;
                              const targetSlide =
                                nextSlide >= totalSlides ? totalSlides - 1 : nextSlide;
                              slider.scrollTo({
                                left: slideWidth * targetSlide,
                                behavior: 'smooth',
                              });
                              setCurrentSlide(targetSlide);
                            }
                          }}
                        >
                          <svg
                            xmlns='http://www.w3.org/2000/svg'
                            className='h-5 w-5'
                            viewBox='0 0 20 20'
                            fill='currentColor'
                          >
                            <path
                              fillRule='evenodd'
                              d='M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z'
                              clipRule='evenodd'
                            />
                          </svg>
                        </button>
                      </div>

                      {/* Navigation dots */}
                      <div className='flex justify-center mt-2 gap-1'>
                        {Array.from({ length: 1 + (record?.image?.sideImages?.length || 0) }).map(
                          (_, index) => (
                            <button
                              key={index}
                              className={`w-2 h-2 rounded-full focus:outline-none transition-colors ${
                                index === currentSlide
                                  ? 'bg-primary dark:bg-primary'
                                  : 'bg-gray-300 dark:bg-gray-600'
                              }`}
                              onClick={() => {
                                const slider = document.querySelector('.snap-x');
                                if (slider) {
                                  const slideWidth = slider.clientWidth;
                                  slider.scrollTo({
                                    left: slideWidth * index,
                                    behavior: 'smooth',
                                  });
                                  setCurrentSlide(index);
                                }
                              }}
                            />
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Description
                  </h4>
                  <p className='text-base leading-relaxed'>
                    {record?.AssetDetials?.detials.description}
                  </p>
                </div>
                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Start Date
                  </h4>
                  <p className='text-lg font-medium'>
                    {new Date(record?.startTime).toLocaleDateString()}
                  </p>
                </div>
                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    End Date
                  </h4>
                  <p className='text-lg font-medium'>
                    {new Date(record?.saleEndDate).toLocaleDateString()}
                  </p>
                </div>

                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Asset Price
                  </h4>
                  <p className='text-lg font-medium'>{record?.assetPrice}</p>
                </div>

                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Total Minted Tokens
                  </h4>
                  <p className='text-lg font-medium'>{record?.maxSupply}</p>
                </div>

                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    City
                  </h4>
                  <p className='text-lg font-medium'>{record?.city}</p>
                </div>

                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Symbol
                  </h4>
                  <p className='text-lg font-medium'>{record?.symbol}</p>
                </div>

                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Platform Fee Percentage
                  </h4>
                  <p className='text-lg font-medium'>{record?.platformFeePercentage}</p>
                </div>
              </div>
            </TabsContent>

            <TabsContent value='transaction' className='space-y-4'>
              <div className='grid gap-4'>
                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Asset Address
                  </h4>
                  <p className='break-all font-mono text-sm'>{record?.assetAddress}</p>
                </div>
                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-4 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-1'>
                    Transaction Hash
                  </h4>
                  <p className='break-all font-mono text-sm'>{record?.txHash}</p>
                </div>
              </div>
            </TabsContent>

            {/* <TabsContent value='yield' className='space-y-6'>
              <div className='grid gap-6'>
                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-6 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-4'>
                    Update Yield
                  </h4>
                  <div className='flex items-center gap-6 mt-2'>
                    <Input
                      placeholder='Enter yield percentage'
                      type='number'
                      min={0}
                      max={100}
                      value={yieldValue}
                      onChange={handleYieldChange}
                      className='max-w-[200px] bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus:ring-primary dark:focus:ring-primary'
                    />
                    <Button
                      variant='default'
                      onClick={updateYield}
                      className='bg-primary hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md'
                    >
                      Update Yield
                    </Button>
                  </div>
                </div>
                <div className='bg-gray-50/50 dark:bg-gray-800/50 rounded-lg p-6 transition-colors'>
                  <h4 className='text-sm font-normal text-gray-500 dark:text-gray-400 mb-4'>
                    Set Yield Reward
                  </h4>
                  <div className='flex items-center gap-6 mt-2'>
                    <Input
                      placeholder='Enter reward in USDT'
                      type='number'
                      min={0}
                      value={rewardValue}
                      onChange={handleRewardChange}
                      className='max-w-[200px] bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 focus:ring-primary dark:focus:ring-primary'
                    />
                    <Button
                      variant='default'
                      onClick={addReward}
                      className='bg-primary hover:bg-primary/90 text-white dark:bg-primary/80 dark:hover:bg-primary/70 dark:text-black/90 transition-colors duration-200 font-medium shadow-sm hover:shadow-md'
                    >
                      Add Reward
                    </Button>
                  </div>
                </div>
              </div>
            </TabsContent> */}
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default AssetShow;
